import { Callout } from '@radix-ui/themes';
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import { createContext, type ReactNode, use, useCallback, useMemo, useRef, useState } from 'react';

export type ToastKind = 'info' | 'success' | 'error';

export interface ToastInput {
  title: string;
  description?: string;
  kind?: ToastKind;
  /** Auto-dismiss delay in ms; 0 keeps the toast until dismissed. Default 5000. */
  duration?: number;
}

interface ToastItem extends ToastInput {
  id: number;
}

interface ToastApi {
  toast: (input: ToastInput) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const ICONS = { info: Info, success: CircleCheck, error: CircleAlert } as const;
const COLORS = { info: 'blue', success: 'green', error: 'red' } as const;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const toast = useCallback(
    (input: ToastInput) => {
      const id = nextId.current++;
      setItems((current) => [...current, { ...input, id }]);
      const duration = input.duration ?? 5000;
      if (duration > 0) setTimeout(() => dismiss(id), duration);
    },
    [dismiss],
  );

  const api = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <section
        className="pointer-events-none fixed right-4 bottom-4 z-[100] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
        aria-label="Notifications"
      >
        {items.map((item) => {
          const kind = item.kind ?? 'info';
          const Icon = ICONS[kind];
          return (
            <Callout.Root
              key={item.id}
              color={COLORS[kind]}
              variant="surface"
              role={kind === 'error' ? 'alert' : 'status'}
              className="pointer-events-auto shadow-lg"
            >
              <Callout.Icon>
                <Icon size={16} />
              </Callout.Icon>
              <Callout.Text>
                <strong>{item.title}</strong>
                {item.description ? <span className="block">{item.description}</span> : null}
              </Callout.Text>
              <button
                type="button"
                aria-label="Dismiss notification"
                className="focus-ring ml-auto rounded p-1 opacity-70 hover:opacity-100"
                onClick={() => dismiss(item.id)}
              >
                <X size={14} />
              </button>
            </Callout.Root>
          );
        })}
      </section>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = use(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
